import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { Copy, Download, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import {
  formatPresenceStatus,
  isExtensionRegistered,
  onlineDeviceChips,
  onlineDeviceCount,
  summarizeRegistration,
  type OnlineStatus,
} from '@/lib/yeastar/presence';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';
import { Modal } from '@/components/ui/Modal';
import { ProvisioningTable } from './ProvisioningTable';

type Extension = {
  id: number;
  number: string;
  first_name?: string;
  caller_id_name?: string;
  email_addr?: string;
  presence_status?: string;
  custom_presence_status?: string;
  status?: string;
  online_status?: OnlineStatus;
};

type BulkAction = 'create' | 'update' | 'skip';
type SortKey = 'number' | 'name' | 'email' | 'presence' | 'registration' | 'devices';

interface ExtensionsTabProps {
  customerId: string;
}

export function ExtensionsTab({ customerId }: ExtensionsTabProps) {
  const { canManage } = useModuleAccess('extensions');

  const extensionsList = useAction(backendApi.provisioning.extensionsList);
  const extensionsCreate = useAction(backendApi.provisioning.extensionsCreate);
  const extensionsDelete = useAction(backendApi.provisioning.extensionsDelete);
  const extensionsCredentials = useAction(backendApi.provisioning.extensionsCredentials);
  const extensionsSync = useAction(backendApi.provisioning.extensionsSync);

  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [registrationFilter, setRegistrationFilter] = useState<
    'all' | 'registered' | 'unregistered'
  >('all');
  const [sortKey, setSortKey] = useState<SortKey>('number');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const registrationSummary = useMemo(() => summarizeRegistration(extensions), [extensions]);

  const [modal, setModal] = useState<null | 'create' | 'delete' | 'bulk'>(null);
  const [createNumber, setCreateNumber] = useState('');
  const [createName, setCreateName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Extension | null>(null);

  const [bulkStep, setBulkStep] = useState<1 | 2 | 3>(1);
  const [bulkText, setBulkText] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState({ created: 0, updated: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const exts = await extensionsList({ customerId, source: 'ui' });
      setExtensions(exts);
    } catch (error) {
      setExtensions([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load extensions');
    } finally {
      setLoading(false);
    }
  }, [customerId, extensionsList]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = extensions.filter((e) => {
      const registered = isExtensionRegistered(e.online_status);
      if (registrationFilter === 'registered' && !registered) return false;
      if (registrationFilter === 'unregistered' && registered) return false;
      const name = e.caller_id_name || e.first_name || '';
      const email = e.email_addr || '';
      if (!q) return true;
      return (
        e.number.includes(q) ||
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q)
      );
    });

    return rows.slice().sort((a, b) => {
      let comparison = 0;
      if (sortKey === 'number') {
        comparison = a.number.localeCompare(b.number, undefined, { numeric: true });
      } else if (sortKey === 'name') {
        comparison = (a.caller_id_name || a.first_name || '').localeCompare(
          b.caller_id_name || b.first_name || '',
          undefined,
          { sensitivity: 'base' }
        );
      } else if (sortKey === 'email') {
        comparison = (a.email_addr || '').localeCompare(b.email_addr || '', undefined, {
          sensitivity: 'base',
        });
      } else if (sortKey === 'presence') {
        comparison = formatPresenceStatus(
          a.presence_status,
          a.custom_presence_status
        ).localeCompare(
          formatPresenceStatus(b.presence_status, b.custom_presence_status),
          undefined,
          { sensitivity: 'base' }
        );
      } else if (sortKey === 'registration') {
        comparison =
          Number(isExtensionRegistered(a.online_status)) -
          Number(isExtensionRegistered(b.online_status));
      } else if (sortKey === 'devices') {
        comparison =
          onlineDeviceCount(a.online_status) - onlineDeviceCount(b.online_status);
      }

      if (comparison === 0) {
        comparison = a.number.localeCompare(b.number, undefined, { numeric: true });
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [extensions, registrationFilter, search, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function closeModal() {
    setModal(null);
    setBulkStep(1);
    setBulkRunning(false);
    setBulkDone(false);
    setBulkProgress(0);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const num = createNumber.trim();
    const name = createName.trim();
    if (!num || !name) return;
    try {
      await extensionsCreate({ customerId, source: 'ui', number: num, name });
      toast.success(`Extension ${num} created`);
      closeModal();
      setCreateNumber('');
      setCreateName('');
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Create failed');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await extensionsDelete({
        customerId,
        source: 'ui',
        id: deleteTarget.id,
        number: deleteTarget.number,
      });
      toast.success(`Extension ${deleteTarget.number} deleted`);
      closeModal();
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Delete failed');
    }
  }

  async function copyCreds(ext: Extension) {
    try {
      const all = await extensionsCredentials({ customerId, source: 'ui' });
      const cred = all.find((c: { extension: string }) => c.extension === ext.number);
      if (!cred) {
        toast.error('No stored credentials for this extension');
        return;
      }
      const text = `Extension: ${ext.number}\nReg name: ${cred.regName}\nSIP password: ${cred.userPassword}\nReg password: ${cred.regPassword}`;
      await navigator.clipboard.writeText(text);
      toast.success(`Credentials copied for extension ${ext.number}`);
    } catch {
      toast.error('Could not load credentials');
    }
  }

  async function exportCredentials() {
    try {
      const creds = await extensionsCredentials({ customerId, source: 'ui' });
      const blob = new Blob([JSON.stringify(creds, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'extension-credentials.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Credentials exported for all extensions');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export failed');
    }
  }

  function parseBulk(): Array<{ number: string; name: string; action: BulkAction }> {
    const lines = bulkText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    return lines.map((line) => {
      const [numberRaw, nameRaw] = line.split(',').map((s) => s?.trim());
      if (!numberRaw || !nameRaw || !/^\d+$/.test(numberRaw)) {
        return { number: numberRaw || '?', name: nameRaw || '—', action: 'skip' as BulkAction };
      }
      if (seen.has(numberRaw)) return { number: numberRaw, name: nameRaw, action: 'skip' as BulkAction };
      seen.add(numberRaw);
      const existing = extensions.find((e) => e.number === numberRaw);
      if (!existing) return { number: numberRaw, name: nameRaw, action: 'create' as BulkAction };
      const currentName = existing.caller_id_name || existing.first_name || '';
      return {
        number: numberRaw,
        name: nameRaw,
        action: currentName === nameRaw ? ('skip' as BulkAction) : ('update' as BulkAction),
      };
    });
  }

  const bulkParsed = bulkStep >= 2 ? parseBulk() : [];
  const bulkCreateCount = bulkParsed.filter((r) => r.action === 'create').length;
  const bulkUpdateCount = bulkParsed.filter((r) => r.action === 'update').length;
  const bulkSkipCount = bulkParsed.filter((r) => r.action === 'skip').length;

  async function runBulk() {
    setBulkStep(3);
    setBulkRunning(true);
    setBulkProgress(0);
    const items = bulkParsed
      .filter((r) => r.action !== 'skip')
      .map((r) => ({ number: r.number, name: r.name }));

    const tick = setInterval(() => {
      setBulkProgress((p) => Math.min(90, p + 15));
    }, 180);

    try {
      const data = await extensionsSync({ customerId, source: 'ui', items });
      clearInterval(tick);
      setBulkProgress(100);
      setBulkRunning(false);
      setBulkDone(true);
      setBulkResult({ created: data.created?.length ?? 0, updated: data.updated?.length ?? 0 });
      void load();
    } catch (error) {
      clearInterval(tick);
      setBulkRunning(false);
      toast.error(error instanceof Error ? error.message : 'Sync failed');
    }
  }

  const filterChips = [
    ['all', `All (${registrationSummary.total})`],
    ['registered', `Registered (${registrationSummary.registered})`],
    ['unregistered', `Unregistered (${registrationSummary.unregistered.length})`],
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mr-1">
          Extensions
        </h3>

        <div className="w-full sm:w-52">
          <Input
            placeholder="Search extensions..."
            aria-label="Search extensions by number, name, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="!py-1.5 text-sm"
          />
        </div>

        <div className="flex rounded-lg p-1 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {filterChips.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setRegistrationFilter(key)}
              className={`h-7 px-2.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${
                registrationFilter === key
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 sm:ml-auto">
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <>
              <Button variant="secondary" size="sm" onClick={exportCredentials}>
                <Download className="h-4 w-4 mr-1" />
                Export credentials
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setModal('bulk')}>
                <Upload className="h-4 w-4 mr-1" />
                Bulk CSV
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setCreateNumber('');
                  setCreateName('');
                  setModal('create');
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create
              </Button>
            </>
          )}
        </div>
      </div>

      <Card animate={false} className="p-0 overflow-hidden">
        {loading ? (
          <Loader text="Loading extensions..." />
        ) : (
          <>
            <div className="hidden md:grid grid-cols-[80px_minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_88px] gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                type="button"
                onClick={() => toggleSort('number')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Number {sortKey === 'number' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('name')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('email')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Email {sortKey === 'email' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('presence')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Presence {sortKey === 'presence' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('registration')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Registration{' '}
                {sortKey === 'registration' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <button
                type="button"
                onClick={() => toggleSort('devices')}
                className="text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                Devices {sortKey === 'devices' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </button>
              <div />
            </div>

            {filtered.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No extensions match your search.
              </p>
            ) : (
              filtered.map((e) => {
                const registered = isExtensionRegistered(e.online_status);
                const endpointCount = onlineDeviceCount(e.online_status);
                const deviceChips = onlineDeviceChips(e.online_status);
                const name = e.caller_id_name || e.first_name || '—';
                const email = e.email_addr?.trim() || '';
                const presence = formatPresenceStatus(
                  e.presence_status,
                  e.custom_presence_status
                );

                return (
                  <div
                    key={e.id}
                    className="grid grid-cols-1 md:grid-cols-[80px_minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_88px] gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 items-center bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <div className="font-mono font-semibold text-gray-900 dark:text-white">
                      {e.number}
                    </div>
                    <div className="truncate text-gray-900 dark:text-white" title={name}>
                      {name}
                    </div>
                    <div
                      className={`text-sm break-all ${
                        email
                          ? 'text-gray-600 dark:text-gray-300'
                          : 'text-gray-400 italic'
                      }`}
                      title={email || undefined}
                    >
                      {email || '—'}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">
                      {presence === '—' ? (
                        <span className="text-gray-400 italic">—</span>
                      ) : (
                        <Badge variant="default">{presence}</Badge>
                      )}
                    </div>
                    <div>
                      <Badge variant={registered ? 'success' : 'default'}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5 inline-block" />
                        {registered ? 'Registered' : 'Unregistered'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap text-gray-500 dark:text-gray-400">
                      <span className="font-mono text-sm">{endpointCount}</span>
                      {deviceChips.map((chip) => (
                        <Badge key={chip} variant="default" className="text-[10px] px-1.5 py-0">
                          {chip}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1 justify-end">
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Copy credentials"
                            onClick={() => void copyCreds(e)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Delete"
                            onClick={() => {
                              setDeleteTarget(e);
                              setModal('delete');
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </Card>

      <Modal isOpen={modal === 'create'} onClose={closeModal} title="Create extension" size="sm">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Extension number"
            value={createNumber}
            onChange={(e) => setCreateNumber(e.target.value)}
            placeholder="e.g. 207"
            required
          />
          <Input
            label="Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="e.g. Naledi Khumalo"
            required
          />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit">Create extension</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={modal === 'delete' && Boolean(deleteTarget)}
        onClose={closeModal}
        title={`Delete extension ${deleteTarget?.number}?`}
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          This permanently removes the extension and its SIP credentials. Any phone registered to it
          will stop working. This cannot be undone.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void confirmDelete()}>
            Delete extension
          </Button>
        </div>
      </Modal>

      <Modal isOpen={modal === 'bulk'} onClose={closeModal} title="Bulk create from CSV" size="lg">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Paste rows as <span className="font-mono">number,name</span> — one extension per line.
          </p>
          <span className="text-xs font-semibold text-gray-500">Step {bulkStep} of 3</span>
        </div>

        {bulkStep === 1 && (
          <>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm p-3"
              placeholder="207,Naledi Khumalo&#10;208,John Smith"
            />
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={() => setBulkStep(2)} disabled={!bulkText.trim()}>
                Preview changes
              </Button>
            </div>
          </>
        )}

        {bulkStep === 2 && (
          <>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {bulkCreateCount} to create · {bulkUpdateCount} to update · {bulkSkipCount} to skip
            </p>
            <ProvisioningTable
              columns={[
                { key: 'number', label: 'Number' },
                { key: 'name', label: 'Name' },
                { key: 'action', label: 'Action' },
              ]}
              rows={bulkParsed.map((r) => ({
                number: <span className="font-mono font-semibold">{r.number}</span>,
                name: r.name,
                action: (
                  <Badge
                    variant={
                      r.action === 'create'
                        ? 'success'
                        : r.action === 'update'
                          ? 'info'
                          : 'warning'
                    }
                  >
                    {r.action === 'create' ? 'Create' : r.action === 'update' ? 'Update' : 'Skip'}
                  </Badge>
                ),
              }))}
            />
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="secondary" onClick={() => setBulkStep(1)}>
                Back
              </Button>
              <Button onClick={() => void runBulk()}>
                Apply {bulkCreateCount + bulkUpdateCount} change
                {bulkCreateCount + bulkUpdateCount === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}

        {bulkStep === 3 && bulkRunning && (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="w-full h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full bg-primary-600 transition-all"
                style={{ width: `${bulkProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Applying changes… {bulkProgress}%
            </p>
          </div>
        )}

        {bulkStep === 3 && bulkDone && (
          <div className="py-6 flex flex-col items-center gap-4 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300 flex items-center justify-center text-xl">
              ✓
            </div>
            <p className="font-semibold text-gray-900 dark:text-white">
              {bulkResult.created} extension{bulkResult.created === 1 ? '' : 's'} created,{' '}
              {bulkResult.updated} updated
            </p>
            <Button onClick={closeModal}>Done</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
